import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import useReviewSystemV3Experiment from '@src/quince-site-common/src/hooks/useReviewSystemV3Experiment'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'

import ProductReviewHighLight, { ProductReviewHighLightProps } from '../productReviewHighlight'

import { MAX_REVIEW_HIGHLIGHTS } from './reviewHighLightsSection.constants'
import { showReviewSection } from './reviewHighLightsSection.helpers'
import stylesV2 from './reviewHighlightsSectionV2.module.scss'
import ReviewHighlightsSummary from './ReviewHighlightsSummary'

export interface ReviewHighLightsSectionProps {
  header?: string
  highLights: ProductReviewHighLightProps[]
  className?: string
  product: Product | Bundle
}

const ReviewHighLightsSection = ({ highLights = [], ...props }: ReviewHighLightsSectionProps) => {
  const { t } = useTranslation('common')
  const size = highLights.length
  const arr = size > MAX_REVIEW_HIGHLIGHTS ? highLights.slice(0, MAX_REVIEW_HIGHLIGHTS) : highLights
  const finalStyles = stylesV2
  const classes = classnames(finalStyles.container, props.className)

  const averageRating = props.product.productReviewSummary?.averageRating
  const reviewCount = props.product.productReviewSummary?.reviewCount

  const { showReviewSummary, showReviewSummaryAndFilterTags } = useReviewSystemV3Experiment()
  const shouldShowReviewSummary = showReviewSummary || showReviewSummaryAndFilterTags

  const getIsVisibleTrackParams = () => {
    return {
      action: 'pdp_accordion_reviews',
      value: true,
      context: {
        productId: props.product.spid,
        productName: props.product.title,
        ...(shouldShowReviewSummary && {
          review_impression_count: arr.length,
          // eslint-disable-next-line i18next/no-literal-string
          review_section_type: 'Review Highlights',
        }),
      },
    }
  }

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  return (
    <div ref={trackIsVisibleRef} className={classes}>
      <div
        className={classnames(finalStyles.container__wrapper, {
          [`${finalStyles.container__wrapper__noBorderBottom}`]: !props.product?.details,
        })}
      >
        <div
          className={classnames(
            finalStyles.container__wrapper__header,
            finalStyles.container__wrapper__header__withSummary,
          )}
        >
          {props.header ?? (
            <span className={finalStyles.container__wrapper__header__title}>{t('review_highlights')}</span>
          )}
          <ReviewHighlightsSummary
            averageRating={averageRating}
            reviewCount={reviewCount}
            showReviewSection={showReviewSection}
          />
        </div>
        {arr.map((highLight, index) => {
          return (
            <ProductReviewHighLight
              key={index}
              {...highLight}
              className={finalStyles.container__wrapper__reviewHighLight}
            />
          )
        })}

        <div className={finalStyles.container__wrapper__seeAllReviews} onClick={showReviewSection}>
          <span>{t('see_all_reviews')}</span>
        </div>
      </div>
    </div>
  )
}

export default ReviewHighLightsSection
